/// MetricsPanel: Execution metrics dashboard panel.
/// Displays 6 metric cards in a responsive grid layout
/// with live updates from the debug session.
///
/// Phase 20, Plan 03.

import { Activity, SkipForward, Zap, AlertTriangle, Clock, TrendingUp } from "lucide-react";
import { useDebugStore } from "@/stores/debugStore";
import { useExecutionMetrics } from "@/hooks/useExecutionMetrics";
import type { ReactNode } from "react";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function MetricsPanel() {
  const mode = useDebugStore((s) => s.mode);
  const { metrics, elapsedTime, stepsPerSecond } = useExecutionMetrics();

  if (mode === "idle") {
    return (
      <div className="flex flex-col items-center justify-center h-full text-zinc-500 gap-2">
        <Activity className="h-6 w-6 text-zinc-600" />
        <span className="text-xs">Start a debug session to view metrics</span>
      </div>
    );
  }

  return (
    <div className="p-3 h-full overflow-y-auto">
      <div className="grid grid-cols-2 gap-2">
        <MetricCard
          icon={<Activity className="h-4 w-4" />}
          label="Instance Count"
          value={String(metrics.instanceCount)}
        />
        <MetricCard
          icon={<SkipForward className="h-4 w-4" />}
          label="Completed Steps"
          value={String(metrics.completedSteps)}
        />
        <MetricCard
          icon={<Zap className="h-4 w-4" />}
          label="Active Tokens"
          value={String(metrics.activeTokens)}
        />
        <MetricCard
          icon={<AlertTriangle className="h-4 w-4" />}
          label="Error Count"
          value={String(metrics.errorCount)}
          valueColor={metrics.errorCount > 0 ? "text-red-400" : undefined}
        />
        <MetricCard
          icon={<Clock className="h-4 w-4" />}
          label="Avg Step Duration"
          value={`${metrics.avgStepDurationMs.toFixed(1)}ms`}
        />
        <MetricCard
          icon={<TrendingUp className="h-4 w-4" />}
          label="Steps/Second"
          value={stepsPerSecond.toFixed(1)}
        />
      </div>

      {/* Elapsed time footer */}
      <div className="mt-3 text-center text-xs text-zinc-500">
        Elapsed: {formatElapsedTime(elapsedTime)}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MetricCard
// ---------------------------------------------------------------------------

function MetricCard({
  icon,
  label,
  value,
  valueColor,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  valueColor?: string;
}) {
  return (
    <div className="flex items-center gap-3 rounded-lg bg-zinc-900/60 border border-zinc-700/50 px-3 py-2">
      <div className="text-zinc-500">{icon}</div>
      <div className="flex flex-col min-w-0">
        <span className="text-[10px] text-zinc-500 uppercase tracking-wider">{label}</span>
        <span className={`text-sm font-bold tabular-nums ${valueColor ?? "text-zinc-200"}`}>
          {value}
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatElapsedTime(seconds: number): string {
  if (seconds < 60) return `${seconds.toFixed(0)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}m ${remainingSeconds}s`;
}
